import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import logo from "../static/media/logo.webp";
import mainLogin from "../static/media/main_login.webp";

const PRIMARY = "#06BBC7";
const BLACK = "#111827";

const UpdatePasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken, user, login } = useAuth();

  const [form, setForm] = useState({
    new_password: "",
    confirm_password: "",
  });

  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const token = accessToken || localStorage.getItem("accessToken") || "";
  const email = location.state?.email || user?.email || "";

  useEffect(() => {
    if (!token) {
      navigate("/iniciar-sesion", { replace: true });
    }
  }, [token, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const parseJsonSafe = async (response) => {
    try {
      return await response.json();
    } catch {
      return {};
    }
  };

  const updateLocalUser = () => {
    const nextUser = {
      ...(user || {}),
      requires_password_change: false,
      requiere_cambio_clave: false,
    };

    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "null");

      if (storedUser) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...storedUser,
            requires_password_change: false,
            requiere_cambio_clave: false,
          })
        );
      }
    } catch {}

    if (typeof login === "function") {
      const refresh = localStorage.getItem("refreshToken") || localStorage.getItem("refresh") || "";
      login({
        access: token,
        refresh,
        user: nextUser,
      });
    }

    return nextUser;
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.new_password || !form.confirm_password) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    if (form.new_password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (form.new_password !== form.confirm_password) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    if (!token) {
      setError("No se encontró el token de autenticación.");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch("/api/auth/update-password/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          new_password: form.new_password,
          confirm_password: form.confirm_password,
        }),
      });

      const data = await parseJsonSafe(response);

      if (!response.ok || data.ok === false) {
        setError(data.error || data.detail || "Error al actualizar la contraseña.");
        return;
      }

      const nextUser = updateLocalUser();
      const role = nextUser?.role || user?.role;

      if (["super_admin", "admin", "staff"].includes(role)) {
        navigate("/admin-panel", { replace: true });
        return;
      }

      navigate("/", { replace: true });
    } catch {
      setError("Error de conexión con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f4f7f8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, md: 4 },
        py: 4,
      }}
    >
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 1020,
          minHeight: { xs: "auto", md: 610 },
          borderRadius: { xs: "26px", md: "36px" },
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "0.88fr 1.12fr" },
          bgcolor: "#ffffff",
          border: "1px solid rgba(17, 24, 39, 0.08)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: { xs: "none", md: "block" },
            bgcolor: BLACK,
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={mainLogin}
            alt="Lily's Sunflowers"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgba(17,24,39,0.76), rgba(6,187,199,0.26))",
            }}
          />

          <Stack
            spacing={2}
            sx={{
              position: "absolute",
              left: 34,
              right: 34,
              bottom: 34,
              color: "#ffffff",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: PRIMARY,
              }}
            >
              Seguridad de cuenta
            </Typography>

            <Typography
              component="h1"
              sx={{
                fontSize: { md: 34, lg: 40 },
                lineHeight: 1.08,
                fontWeight: 700,
              }}
            >
              Actualiza tu clave temporal
            </Typography>

            <Typography
              sx={{
                fontSize: 15,
                lineHeight: 1.7,
                color: "rgba(255,255,255,0.84)",
                maxWidth: 380,
              }}
            >
              Para proteger tu cuenta, debes cambiar la contraseña temporal antes
              de continuar usando el sistema.
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2.5, sm: 5, md: 7 },
            py: { xs: 4.5, md: 6 },
          }}
        >
          <Box
            component="form"
            onSubmit={submit}
            sx={{
              width: "100%",
              maxWidth: 410,
            }}
          >
            <Stack spacing={2.6}>
              <Stack spacing={2} alignItems={{ xs: "center", md: "flex-start" }}>
                <Box
                  component="img"
                  src={logo}
                  alt="Lily's Sunflowers"
                  sx={{
                    width: { xs: 108, md: 122 },
                    height: "auto",
                    objectFit: "contain",
                  }}
                />

                <Stack spacing={1} sx={{ textAlign: { xs: "center", md: "left" } }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: PRIMARY,
                    }}
                  >
                    Primer ingreso
                  </Typography>

                  <Typography
                    component="h2"
                    sx={{
                      fontSize: { xs: 28, sm: 34 },
                      lineHeight: 1.12,
                      fontWeight: 700,
                      color: BLACK,
                    }}
                  >
                    Actualizar contraseña
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 15,
                      lineHeight: 1.7,
                      color: "#4b5563",
                    }}
                  >
                    Ingresa una nueva contraseña para continuar.
                  </Typography>
                </Stack>
              </Stack>

              {email && (
                <Alert
                  icon={<LockResetRoundedIcon />}
                  severity="info"
                  sx={{
                    borderRadius: "14px",
                    fontSize: 14,
                    bgcolor: "rgba(6,187,199,0.1)",
                    color: BLACK,
                    "& .MuiAlert-icon": {
                      color: PRIMARY,
                    },
                  }}
                >
                  Cuenta: {email}
                </Alert>
              )}

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    borderRadius: "14px",
                    fontSize: 14,
                  }}
                >
                  {error}
                </Alert>
              )}

              <Stack spacing={2.2}>
                <TextField
                  fullWidth
                  size="small"
                  name="new_password"
                  label="Nueva contraseña"
                  type={showPassword1 ? "text" : "password"}
                  value={form.new_password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  disabled={loading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      bgcolor: "#ffffff",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#4b5563",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#374151",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      borderColor: PRIMARY,
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword1((prev) => !prev)}
                          edge="end"
                          disabled={loading}
                          sx={{ color: "#4b5563" }}
                        >
                          {showPassword1 ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  size="small"
                  name="confirm_password"
                  label="Confirmar contraseña"
                  type={showPassword2 ? "text" : "password"}
                  value={form.confirm_password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  disabled={loading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      bgcolor: "#ffffff",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#4b5563",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#374151",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      borderColor: PRIMARY,
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword2((prev) => !prev)}
                          edge="end"
                          disabled={loading}
                          sx={{ color: "#4b5563" }}
                        >
                          {showPassword2 ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                sx={{
                  height: 46,
                  borderRadius: "14px",
                  bgcolor: PRIMARY,
                  color: "#ffffff",
                  fontSize: 15,
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#05a8b3",
                    boxShadow: "none",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(6,187,199,0.45)",
                    color: "#ffffff",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                ) : (
                  "Guardar contraseña"
                )}
              </Button>

              <Button
                component={RouterLink}
                to="/iniciar-sesion"
                sx={{
                  color: BLACK,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 13.5,
                  borderRadius: "999px",
                  px: 2,
                  py: 0.8,
                  alignSelf: "center",
                  "&:hover": {
                    bgcolor: "rgba(6,187,199,0.08)",
                    color: PRIMARY,
                  },
                }}
              >
                Volver al login
              </Button>
            </Stack>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default UpdatePasswordPage;